/* eslint-disable max-lines, class-methods-use-this */
import { EventBus, Events } from "jiffy-store";
import { track } from "jiffy-analytics";
import AddressAutocomplete from "common/utils/form/address-autocomplete";
import useSvg from "common/utils/ui/use_svg";
import UserContextHelper from "components/jiffy/masthead-redesign2025/local-delivery/utils/user-context-helper";

const BASE_CLASS = "smarty-streets-autocomplete";
const SELECTORS = {
  input: ".js-smarty-streets-autocomplete-input",
  root: ".js-smarty-streets-autocomplete",
  clear: ".js-smarty-streets-autocomplete-clear"
};
const MIXPANEL_EVENTS = {
  INITIALIZED: "smarty_autocomplete_initialized",
  SELECTED: "smarty_autocomplete_selected",
  CLEARED: "smarty_autocomplete_cleared",
  ERROR: "smarty_autocomplete_error",
  SEARCH_STARTED: "address_search_started",
  AUTOSUGGEST_RESULT_DISPLAYED: "address_autosuggest_result_displayed",
  ADDRESS_SELECTED: "address_selected",
  VALIDATION_INITIATED: "address_validation_initiated",
  VALIDATION_COMPLETED: "address_validation_completed"
};

const CLEAR_BUTTON_IS_VISIBLE_CLASS = "smarty-streets-autocomplete__clear-button--is-visible";

/**
 * SmartyStreets address autocomplete component that provides address suggestions
 * as users type. Extends the base AddressAutocomplete class with additional
 * features like clear button, analytics tracking, and custom event handling.
 *
 * Optimizes API usage by filtering saved addresses first before querying Smarty API.
 *
 * @extends AddressAutocomplete
 */
export default class SmartyStreetsAutocomplete extends AddressAutocomplete {
  #savedAddresses = [];

  #searchStartedTracked = false;

  #currentSuggestions = [];

  #validationStartTime = null;

  #currentValidatingAddress = null;

  /**
   * Creates a new SmartyStreetsAutocomplete instance.
   * Initializes the component, binds events, and tracks initialization.
   *
   * @param {Array<Object>} savedAddresses - Array of saved addresses to filter first
   * @throws {Error} Throws an error if the root element is not found in the DOM
   */
  constructor(savedAddresses = []) {
    const $root = document.querySelector(SELECTORS.root);
    if (!$root) {
      throw new Error("SmartyStreetsAutocomplete: Root element not found in DOM");
    }

    super(SELECTORS.root, BASE_CLASS, { submitOnEnter: true });

    // Reduce input length threshold for local delivery
    this.inputLengthThreshold = 4;

    // Reduce max results for local delivery
    this.maxResults = 5;

    // Set saved addresses
    this.#savedAddresses = savedAddresses;

    this.#bindAdditionalEvents();

    this.#trackEvent(MIXPANEL_EVENTS.INITIALIZED, {
      api_key_present: !!this.apiKey,
      page: window.location.pathname,
      saved_addresses_count: savedAddresses.length
    });
  }

  /**
   * Gets the input element
   * @returns {Element} The input element
   * @private
   */
  get #$input() {
    return document.querySelector(SELECTORS.input);
  }

  /**
   * Gets the clear button element
   * @returns {Element} The clear button element
   * @private
   */
  get #$clearButton() {
    return document.querySelector(SELECTORS.clear);
  }

  /**
   * Binds additional event listeners for input and clear button.
   * This is a private method called during initialization.
   *
   * @private
   */
  #bindAdditionalEvents = () => {
    this.#$clearButton?.addEventListener("click", this.#handleClearClick);
    this.#$input?.addEventListener("input", this.#handleInputChange);
  };

  /**
   * Tracks analytics events using Mixpanel.
   * Automatically adds a timestamp to all tracked events.
   *
   * @param {string} eventName - The name of the event to track
   * @param {Object} [data={}] - Additional data to include with the event
   * @private
   */
  #trackEvent = (eventName, data = {}) => {
    track(eventName, { ...data, timestamp: new Date().toISOString() });
  };

  /**
   * Tracks when user starts typing in the address search field
   *
   * Sends analytics event with user context and address counts to understand
   * search behavior and user engagement patterns
   *
   * @private
   */
  #trackSearchStarted = () => {
    const zipcode = UserContextHelper.getSessionZipcode();
    const userType = UserContextHelper.getUserType();

    const verifiedCount = this.#savedAddresses.filter(addr => addr.verifiedAt).length;
    const unverifiedCount = this.#savedAddresses.length - verifiedCount;

    this.#trackEvent(MIXPANEL_EVENTS.SEARCH_STARTED, {
      input_field: "address_line_1",
      user_type: userType,
      pre_populated_zipcode: zipcode,
      component_type: "modal",
      saved_address_count: this.#savedAddresses.length,
      verified_address_count: verifiedCount,
      unverified_address_count: unverifiedCount
    });
  };

  /**
   * Creates a simple hash of the address for tracking purposes
   *
   * @param {Object} result - The address object
   * @returns {string} Hash of the address
   * @private
   */
  #createAddressHash = result => {
    const addressString = [
      result.street_line || "",
      result.secondary || "",
      result.city || "",
      result.state || "",
      result.zipcode || ""
    ]
      .join("|")
      .toLowerCase()
      .trim();

    // Simple hash function
    /* eslint-disable no-bitwise, no-plusplus */
    let hash = 0;
    for (let i = 0; i < addressString.length; i++) {
      const char = addressString.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash &= hash; // Convert to 32bit integer
    }
    /* eslint-enable no-bitwise, no-plusplus */
    return Math.abs(hash).toString(16);
  };

  /**
   * Gets selection metadata for tracking
   * @param {Object} result - Selected address
   * @returns {Object} Metadata object
   * @private
   */
  #getSelectionMetadata = result => {
    const isSaved = result.isSaved === true;
    let selectionSource = "smartystreets_api";
    let verificationStatus = "new";
    let addressId = null;

    if (isSaved && result.savedAddressId) {
      addressId = String(result.savedAddressId);
      const savedAddress = this.#savedAddresses.find(({ id }) => id === result.savedAddressId);

      selectionSource = savedAddress?.verifiedAt ? "saved_verified" : "saved_unverified";
      verificationStatus = savedAddress?.verifiedAt ? "verified" : "unverified";
    }

    return { selectionSource, verificationStatus, addressId };
  };

  /**
   * Tracks when user selects an address from the autocomplete
   *
   * Determines selection source, position, and verification status to understand
   * user behavior and address selection patterns
   *
   * @param {Object} result - The selected address result
   * @private
   */
  #trackAddressSelected = result => {
    const sessionZipcode = UserContextHelper.getSessionZipcode();

    const selectionPosition =
      this.#currentSuggestions.findIndex(
        suggestion =>
          suggestion.street_line === result.street_line &&
          suggestion.city === result.city &&
          suggestion.zipcode === result.zipcode
      ) + 1;

    const { selectionSource, verificationStatus, addressId } = this.#getSelectionMetadata(result);

    const trackingData = {
      selection_source: selectionSource,
      selection_position: selectionPosition || 1,
      zipcode: result.zipcode || sessionZipcode,
      address_verification_status: verificationStatus,
      requires_2fa: false,
      component_type: "modal"
    };

    if (addressId) {
      trackingData.address_id = addressId;
    }

    this.#trackEvent(MIXPANEL_EVENTS.ADDRESS_SELECTED, trackingData);
  };

  /**
   * Tracks when address validation is initiated
   *
   * @param {Object} result - The address object being validated
   * @private
   */
  #trackValidationInitiated = result => {
    const isSaved = result.isSaved === true;
    const entryMethod = isSaved ? "saved" : "autosuggest";
    const addressHash = this.#createAddressHash(result);

    // Store validation start time and address for completion tracking
    this.#validationStartTime = performance.now();
    this.#currentValidatingAddress = {
      ...result,
      entryMethod
    };

    this.#trackEvent(MIXPANEL_EVENTS.VALIDATION_INITIATED, {
      entry_method: entryMethod,
      zipcode: result.zipcode || "",
      address_hash: addressHash,
      component_type: "modal"
    });
  };

  /**
   * Filters saved addresses based on input text.
   * Matches against street address, city, state, and zipcode.
   *
   * @param {string} input - The search input text
   * @returns {Array<Object>} Filtered addresses converted to Smarty format
   * @private
   */
  #filterSavedAddresses = input => {
    const searchTerm = input.toLowerCase().trim();

    return this.#savedAddresses
      .filter(address => {
        const address1 = (address.address1 || "").toLowerCase();
        const city = (address.city || "").toLowerCase();
        const state = (address.state || "").toLowerCase();
        const zipcode = (address.zipcode || "").toLowerCase();

        return (
          address1.includes(searchTerm) ||
          city.includes(searchTerm) ||
          state.includes(searchTerm) ||
          zipcode.includes(searchTerm)
        );
      })
      .map(address => ({
        street_line: address.address1 || "",
        secondary: address.address2 || "",
        city: address.city || "",
        state: address.state || "",
        zipcode: address.zipcode || "",
        entries: 0,
        isSaved: true,
        savedAddressId: address.id
      }));
  };

  /**
   * Handles search with optimized behavior:
   * 1. First filters saved addresses (requires 4+ characters)
   * 2. Only queries Smarty API if no saved addresses match
   *
   * @param {string} input - The search input text
   * @returns {Promise<Array<Object>>} Array of address suggestions
   * @override
   */
  async onSearch(input) {
    if (!input || input.length < this.inputLengthThreshold) {
      this.#currentSuggestions = [];
      return [];
    }

    // First, try to filter saved addresses
    const savedMatches = this.#filterSavedAddresses(input);

    if (savedMatches.length > 0) {
      this.#trackEvent("saved_addresses_filtered", {
        query: input,
        matches_count: savedMatches.length
      });
      this.#currentSuggestions = savedMatches;
      return savedMatches;
    }

    // Only query Smarty API if no saved addresses match
    const startTime = performance.now();
    const smartyResults = await super.onSearch(input);
    const endTime = performance.now();
    const responseTime = Math.round(endTime - startTime);

    // Track autosuggest results from SmartyStreets API
    if (smartyResults && smartyResults.length >= 0) {
      this.#trackEvent(MIXPANEL_EVENTS.AUTOSUGGEST_RESULT_DISPLAYED, {
        suggestion_count: smartyResults.length,
        query: input,
        response_time_ms: responseTime,
        api_source: "smartystreets"
      });
    }

    this.#currentSuggestions = smartyResults;
    return smartyResults;
  }

  /**
   * Handles input field changes to show/hide the clear button.
   * Tracks the address_search_started event on first input.
   * @param {Event} e - The input event object
   * @private
   */
  #handleInputChange = e => {
    const value = e.target?.value ?? "";
    this.#setClearButtonVisibility(value.trim().length > 0);

    // Track search started event only once
    if (!this.#searchStartedTracked && value.trim().length > 0) {
      this.#trackSearchStarted();
      this.#searchStartedTracked = true;
    }
  };

  /**
   * Handles clear button click events.
   * @param {Event} e - The click event object
   * @private
   */
  #handleClearClick = e => {
    e.preventDefault();
    this.#clearInput();
  };

  /**
   * Handles address selection from autocomplete suggestions.
   * Calls parent onSubmit, tracks analytics, and publishes event after delay.
   * @param {Object} result - The selected address result
   * @param {string} result.street_line - The street address
   * @param {string} [result.secondary] - Secondary address info (apt, suite, etc.)
   * @param {string} result.city - The city name
   * @param {string} result.state - The state abbreviation
   * @param {string} result.zipcode - The ZIP code
   * @param {number} [result.entries] - Number of units at this address
   * @override
   */
  onSubmit = result => {
    if (!result) return;

    super.onSubmit(result);

    const fullAddress = {
      street_line: result.street_line,
      secondary: result.secondary || "",
      city: result.city,
      state: result.state,
      zipcode: result.zipcode,
      entries: result.entries || 0
    };

    this.#trackEvent(MIXPANEL_EVENTS.SELECTED, fullAddress);
    this.#trackAddressSelected(result);
    this.#trackValidationInitiated(result);

    EventBus.publish(Events.SS_AUTOCOMPLETE_ADDRESS_SELECTED, this, fullAddress);
  };

  /**
   * Renders a single autocomplete suggestion item.
   * Creates HTML for address suggestion with icon and formatted text.
   * Saved addresses are visually distinguished with a checkmark icon and CSS class.
   *
   * @param {Object} result - The address suggestion data
   * @param {string} result.street_line - The street address
   * @param {string} result.city - The city name
   * @param {string} result.state - The state abbreviation
   * @param {string} result.zipcode - The ZIP code
   * @param {number} [result.entries] - Number of units at this address
   * @param {boolean} [result.isSaved] - Whether this is a saved address
   * @param {Object} props - HTML attributes to apply to the list item
   * @returns {string} HTML string for the suggestion list item
   * @override
   */
  renderItem = (result, props) => {
    const unitsText = result.entries > 1 ? ` (+${result.entries} units)` : "";
    const icon = result.isSaved ? "marker-pin-check" : "marker";

    const propsString = Object.entries(props)
      .map(([key, value]) => {
        if (key === "class") {
          const savedClass = result.isSaved
            ? " smarty-streets-autocomplete__suggestion-item--saved"
            : "";
          return `class="${value} smarty-streets-autocomplete__suggestion-item${savedClass}"`;
        }
        return `${key}="${value}"`;
      })
      .join(" ");

    return `
      <li ${propsString} data-testid="smarty-suggestion-item" data-saved="${!!result.isSaved}">
        ${useSvg(icon, { class: "smarty-streets-autocomplete__suggestion-icon" })}
        <div class="smarty-streets-autocomplete__suggestion-text-wrapper">
          <p class="smarty-streets-autocomplete__suggestion-text smarty-streets-autocomplete__suggestion-text--primary">
            ${result.street_line}${unitsText}
          </p>
          <p class="smarty-streets-autocomplete__suggestion-text smarty-streets-autocomplete__suggestion-text--secondary">
            ${result.city}, ${result.state} ${result.zipcode}
          </p>
        </div>
      </li>
    `;
  };

  /**
   * Updates the saved addresses for filtering.
   * Allows dynamic updates when addresses are added or changed.
   *
   * @param {Array<Object>} addresses - Array of saved addresses
   * @public
   */
  updateSavedAddresses(addresses) {
    this.#savedAddresses = addresses || [];
    this.#trackEvent("saved_addresses_updated", {
      count: this.#savedAddresses.length
    });
  }

  /**
   * Tracks when address validation completes successfully
   *
   * Should be called by external components when address validation succeeds
   *
   * @param {Object} options - Validation completion options
   * @param {boolean} [options.isLocalDeliveryEligible=true] - Whether address is eligible for local delivery
   * @public
   */
  trackValidationCompleted({ isLocalDeliveryEligible = true } = {}) {
    if (!this.#validationStartTime || !this.#currentValidatingAddress) {
      return;
    }

    const validationTime = Math.round(performance.now() - this.#validationStartTime);

    this.#trackEvent(MIXPANEL_EVENTS.VALIDATION_COMPLETED, {
      entry_method: this.#currentValidatingAddress.entryMethod || "autosuggest",
      zipcode: this.#currentValidatingAddress.zipcode || "",
      validation_time_ms: validationTime,
      is_local_delivery_eligible: isLocalDeliveryEligible,
      component_type: "modal"
    });

    // Reset validation tracking state
    this.#validationStartTime = null;
    this.#currentValidatingAddress = null;
  }

  /**
   * Sets the visibility of the clear button.
   * @param {boolean} visible - Whether the clear button should be visible
   * @private
   */
  #setClearButtonVisibility = visible => {
    if (!this.#$clearButton) return;
    this.#$clearButton.classList.toggle(CLEAR_BUTTON_IS_VISIBLE_CLASS, visible);
  };

  /**
   * Clears the input field and resets the component state.
   * @private
   */
  #clearInput = () => {
    if (!this.#$input) return;
    this.#$input.value = "";
    this.#setClearButtonVisibility(false);
    this.#searchStartedTracked = false;
    this.#$input.focus();
    EventBus.publish(Events.SS_AUTOCOMPLETE_ADDRESS_CLEARED, this);
  };

  /**
   * Public method to clear the input field and reset the component state.
   * @public
   */
  clearInput() {
    this.#clearInput();
  }

  /**
   * Cleans up the component by removing event listeners.
   * Should be called when the component is no longer needed to prevent memory leaks.
   *
   * @override
   */
  destroy = () => {
    this.#$clearButton?.removeEventListener("click", this.#handleClearClick);
    this.#$input?.removeEventListener("input", this.#handleInputChange);
    super.destroy();
  };
}
